import { TError, TFetchStatus } from 'models';
import React, { useEffect, useState } from 'react';
import { PageStyled } from 'app/Pages/common/styles';
import IDDList from 'app/Pages/IDD/List';
import { isIframe, preflightAPICall, processAPIError, translate } from 'utils';
import { getPoliciesLinks, IPageIDD, setPoliciesLinks, TPoliciesWithLink } from 'app/Pages/IDD';
import IDD, { setCustomerData } from 'app/Components/IDD';
import axios from 'axios';
import { TCustomer } from 'app/Pages/Clients/common';
import { renderErrors } from 'app/Components/common/Error';
import { Button } from '@insly/qmt-reactjs-ui-lib';
import { IDDErrorsWrapper } from 'app/Components/IDD/styles';
import { setListPageSize } from 'app/Pages/common/List';
import apiConfig from 'config/api';

const PageIDD = ({ profile } : { profile: TCustomer }) => {
  const [listDataFetchStatus, updateListDataFetchStatus] = useState<TFetchStatus>('loading');
  const [iddListErrorText, updateIddListErrorText] = useState('');
  const [iddList, updateIddList] = useState<IPageIDD['listData']>({});
  const [searchQuery, updateSearchQuery] = useState('');
  const [section, updateSection] = useState<'list' | 'add' >('list');
  const [policiesWithLink, updatePoliciesWithLink] = useState<TPoliciesWithLink>([]);
  const [errors, setErrors] = useState<TError[]>([]);

  useEffect(() => {
    updateListDataFetchStatus(null);

    if (section === 'list') {
      updateIddList({});
      getList({});
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, searchQuery]);

  useEffect(() => {
    const handler = (event: { data: any }) => {
      if (event.data) {
        setPoliciesLinks(event.data, updatePoliciesWithLink);
      }
    };

    window.addEventListener('message', handler);

    // clean up
    return () => window.removeEventListener('message', handler);

    // eslint-disable-next-line
  }, []);

  const handleSearchChange = (value: string) => {
    updateSearchQuery(value);
  };

  const getList = ({ page }: { page?: number }) => {
    updateListDataFetchStatus('loading');
    let query: Record<string, string | number> = {};

    query.Size = setListPageSize();

    if (page) {
      query.Page = page;
    } else {
      query.Page = 1;
    }

    if (searchQuery) {
      query.Search = searchQuery.trim();
    }

    query.CustomerID = profile.id as string;

    preflightAPICall(() => {
      axios.post(apiConfig.QMT_SEARCH, {
        'data': query,
        'name': 'idd-search'
      }).then(response => {
        const data = response.data;
        onDataLoaded(data);

      }).catch(error => {

        onDataLoaded({
          results: [],
        });

        setErrors(() => processAPIError(error, true) as TError[]);

      });
    });

  };

  const onDataLoaded = (data: IPageIDD['listData']) => {
    updateIddList(data);
    updateListDataFetchStatus('success');
    isIframe() && getPoliciesLinks(data.results || []);
  };

  const handleListChangePage = (page: number) => {
    getList({ page });
  };

  const handleAddIDDClick = () => {
    updateSection('add');
  };

  switch (section) {
    case 'add':
      return (
        <IDD
          customerData={setCustomerData(profile)}
          initialIDDState="idd_requested"
          handleIddCancel={() => updateSection('list')}
        />
      );
    case 'list':
    default:
      return (
        <PageStyled>
          {errors?.length > 0 ? (
            <IDDErrorsWrapper>
              {renderErrors(errors)}
              <Button onClick={() => getList({})}>{translate({ key: 'idd.button.reset' })}</Button>
            </IDDErrorsWrapper>
          ) : (
            <IDDList
              title={translate({ key: 'idd.list.title' })}
              errorText={iddListErrorText}
              step={section}
              dataStatus={listDataFetchStatus}
              itemList={iddList}
              policiesWithLink={policiesWithLink}
              handleSearchChange={handleSearchChange}
              handleListChangePage={handleListChangePage}
              handleAddIDDClick={handleAddIDDClick}
              hideCustomerInfo={true}
              updateIddList={updateIddList}
              updateIddErrorText={updateIddListErrorText}
              updateDataFetchStatus={updateListDataFetchStatus}
            />
          )}
        </PageStyled>
      );
  }

};

export default PageIDD;
